import {
  getFiguresForModel,
  getDrawBox2D,
  setupClippingPlane,
  getAllFiguresForModel,
  getFigurePerimeter,
} from "./slice";
import {
  ToothNumberMode,
  getNumberChanges,
  getNumberChangesCount,
} from "../tooth/number/ToothNumberMode";
import { getToothFileName } from "@/components/appStart/adjustModel.js";
import { runSmoothing } from "./smoothing";
import { sendAddToothRequest, sendSmoothStlRequest } from "@/api/toothApi";
import { sendUpdateTeethRequest } from "@/api/toothApi";
import { isNumeric } from "@/components/math";

import * as THREE from "three";
import { STLExporter } from "three/addons/exporters/STLExporter.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import { ConvexGeometry } from "three/addons/geometries/ConvexGeometry.js";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";
import { MarchingCubes } from "three/addons/objects/MarchingCubes.js";
import swal from "sweetalert";
import { saveBlob, saveLocalJson } from "@/components/reports/fileMethods";
import { Apps } from "./DicomManager";

const annotationShapeName = "annotationShape";
const anchorShapeName = "anchorShape";
// const clippingPlane = new THREE.Plane();
const anchorPointsMaxD = 2;
const maxMarchingCubesPolyCount = 1000000; //300000;
const meshBasicMaterial = new THREE.MeshBasicMaterial({
  color: 0xffff00,
  wireframe: true,
  side: 2,
});

function calculateAllFigures(app, dicomBox, force) {
  if (
    app &&
    (!app.isCalculated || force) &&
    app.annotations &&
    app.annotations.length
  ) {
    const drawBox2D = getDrawBox2D(dicomBox, app);
    const coefX = app.baseSize.x / drawBox2D.size.x;
    const coefY = app.baseSize.y / drawBox2D.size.y;

    // let annotations = app.allAnnotations ? app.allAnnotations : app.annotations;
    let annotations = app.annotations;
    annotations = annotations.filter((a) => a.isTooth || a.isBone || a.isNerve);
    console.log("start calculateAllFigures", new Date(), annotations);
    annotations.forEach((annotation) => {
      let allFigures = getAllFiguresForModel(
        annotation.model,
        app,
        dicomBox,
        true,
        force,
      );
      annotation.allFigures = allFigures;
      annotation.allAnnotationPoints = [];
      for (var sliceIndex in annotation.allFigures) {
        let sli = +sliceIndex;
        let figures = annotation.allFigures[sliceIndex];
        if (figures) {
          figures.forEach((figure) => {
            fillKonvaPoints(figure, app, drawBox2D, coefX, coefY);
            figure.annotationPoints = getAnnotationPoints(
              figure,
              app,
              drawBox2D,
              coefX,
              coefY,
            );
            figure.annotationPoints.forEach((p) => (p.z = sli - 1));
            figure.annotationPoints.forEach((p, key) => {
              p.z = sliceIndex - 1;
              p.str = `${p.x},${p.y},${p.z}`;
              p.fstr = key;
              p.figure = figure;
            });
            annotation.allAnnotationPoints.push(
              ...figure.annotationPoints.values(),
            );
          });
        }
      }
      if (annotation.allAnnotationPoints.length) annotation.calculated = true;
    });
    console.log("end calculateAllFigures", new Date());
    app.isCalculated = true;
  }
}

function drawAnnotations(app, dicomBox) {
  if (app && dicomBox) {
    app.isBrushMode = app.editMode === "brush";

    destroyAnnotationShapes(app);

    if ((app.parentApp && !app.showSegmentation) || !app.annotations) {
      return;
    }

    if (!app.parentApp && !app.isCalculated) {
      calculateAllFigures(app, dicomBox);
    }

    const layerGroup = app.getActiveLayerGroup();
    const drawLayer = layerGroup.getActiveDrawLayer();
    const konvaLayer = drawLayer.getKonvaLayer();
    const activeLayer = layerGroup.getActiveViewLayer();
    const viewController = activeLayer.getViewController();
    const view = viewController.getView();

    const drawBox2D = getDrawBox2D(dicomBox, app);
    let sliceIndex = +app.current;
    let clippingPlane = setupClippingPlane(app, dicomBox, sliceIndex);
    let dy = app.index != 0 ? app.worldSize.y - dicomBox.size.y : 0;
    const coefX = app.baseSize.x / drawBox2D.size.x;
    const coefY = app.baseSize.y / drawBox2D.size.y;
    // let annotations = app.allAnnotations ? app.allAnnotations : app.annotations;
    let annotations = app.annotations;
    annotations = annotations.filter(
      (a) => (a.isTooth || a.isBone || a.isNerve) && !a.isHidden,
    );
    annotations = annotations.sort(function (a1, a2) {
      return a1.zIndex - a2.zIndex;
    });
    for (const annotation of annotations) {
      try {
        annotation.figures = [];
        if (!app.parentApp && !annotation.allAnnotationPoints) {
          annotation.allAnnotationPoints = [];
        }
        if (!annotation.allFigures) {
          annotation.allFigures = {};
        }

        if (
          annotation.isHidden ||
          (annotation.parentAnnotation && annotation.parentAnnotation.isHidden)
        ) {
          annotation.hasFigures = false;
          continue;
        }

        // let existedFigures = annotation.allFigures[sliceIndex];
        // let figures = app.parentApp
        //   ? getFiguresFromParentAnnotation(annotation, sliceIndex)
        //   : existedFigures;
        let existedFigures = app.isRotationApplied
          ? null
          : annotation.allFigures[sliceIndex];
        let figures = existedFigures;
        if (app.parentApp) {
          figures = getFiguresFromParentAnnotation(annotation, sliceIndex);
        } else if (app.isRotationApplied) {
          figures = getFiguresForAngles(annotation, view);
        }

        if (!figures || annotation.isNerve)
          figures = getFiguresForModel(
            annotation.model,
            clippingPlane,
            app,
            sliceIndex,
            false,
            dy,
            true,
          );

        if (figures && figures.length) {
          let a_figures = [];
          for (const figure of figures) {
            if (!figure.konvaPoints)
              fillKonvaPoints(figure, app, drawBox2D, coefX, coefY);

            if (!figure.annotationPoints) {
              figure.annotationPoints = getAnnotationPoints(
                figure,
                app,
                drawBox2D,
                coefX,
                coefY,
              );
            }

            figure.filename = annotation.filename;
            figure.color = annotation.opacityColor;
            figure.annotation = annotation;

            let a_figure = {
              filename: annotation.filename,
              color: annotation.opacityColor,
              konvaPoints: figure.konvaPoints,
              annotationPoints: figure.annotationPoints,
              annotation: annotation,
              isStraight: figure.isStraight,
              linkedFigures: figure.linkedFigures,
            };
            a_figures.push(a_figure);
          }
          annotation.figures =
            app.parentApp ||
            annotation.isNerve ||
            !existedFigures ||
            existedFigures.length < 1
              ? a_figures
              : existedFigures;
          annotation.hasFigures = true;
        } else {
          annotation.figures = [];
          annotation.hasFigures = false;
        }
        /// !!!
        if (!app.isRotationApplied)
          annotation.allFigures[sliceIndex] = annotation.figures;
        // if (!existedFigures || existedFigures.length < 1)
        //   annotation.allFigures[sliceIndex] = annotation.figures;
      } catch (error) {
        console.log("annotation error", annotation, error);
      }
    }

    app.setToolFeatures({
      annotations: annotations,
    });

    // for (const annotation of annotations) {
    //   if (!annotation.isHidden && !annotation.parentAnnotation) {
    //     drawNervePoints(app, annotation, konvaLayer);
    //   }
    // }
  }
}

function getFiguresForAngles(annotation, view) {
  let figures = [];
  if (annotation.allAnnotationPoints) {
    // const layerGroup = _app.getActiveLayerGroup();
    // const activeLayer = layerGroup.getActiveViewLayer();
    // const viewController = activeLayer.getViewController();
    // const view = viewController.getView();

    let filteredPoints = new Map();
    for (const point of annotation.allAnnotationPoints) {
      let drPointsArr = view.realPoints.get(point.str);
      if (drPointsArr) {
        for (const drPoint of drPointsArr) {
          const str = `${drPoint.x},${drPoint.y}`;
          filteredPoints.set(str, {
            x: drPoint.x,
            y: drPoint.y,
            mainPoint: point,
          });
        }
      }
    }

    if (filteredPoints.size) {
      // console.log("filteredPoints", filteredPoints);
      let figure = {
        // sliceIndex: i,
        annotation: annotation,
        // color: "rgba(255,0,0,0.5)",
        annotationPoints: filteredPoints,
      };
      figures.push(figure);
    }
  }

  return figures;
}

// !!!
function getFiguresFromParentAnnotation(annotation, sliceIndex) {
  let figures = [];
  if (
    annotation &&
    annotation.parentAnnotation &&
    annotation.parentAnnotation.allAnnotationPoints &&
    sliceIndex
  ) {
    let parent = annotation.parentAnnotation;
    sliceIndex -= 1;
    let figurePoints = [];
    if (annotation.appIndex == 0) {
      if (parent.appIndex == 1) {
      } else if (parent.appIndex == 2) {
        figurePoints = parent.allAnnotationPoints
          .filter((p) => p && p.y == sliceIndex)
          .map((p) => {
            return { x: p.x, y: p.z };
          });
      } else {
        figurePoints = parent.allAnnotationPoints.filter(
          (p) => p && p.z == sliceIndex,
        );
      }
    } else if (annotation.appIndex == 1) {
      if (parent.appIndex == 1) {
        figurePoints = parent.allAnnotationPoints.filter(
          (p) => p && p.z == sliceIndex,
        );
      } else if (parent.appIndex == 2) {
        figurePoints = parent.allAnnotationPoints
          .filter((p) => p && p.x == sliceIndex)
          .map((p) => {
            return { x: p.z, y: p.y };
          });
      } else {
        figurePoints = parent.allAnnotationPoints
          .filter((p) => p && p.x == sliceIndex)
          .map((p) => {
            return { x: p.y, y: p.z };
          });
      }
    } else if (annotation.appIndex == 2) {
      if (parent.appIndex == 1) {
      } else if (parent.appIndex == 2) {
        figurePoints = parent.allAnnotationPoints.filter(
          (p) => p && p.z == sliceIndex,
        );
      } else {
        figurePoints = parent.allAnnotationPoints
          .filter((p) => p && p.y == sliceIndex)
          .map((p) => {
            return { x: p.x, y: p.z };
          });
      }
    }
    if (figurePoints.length > 1) {
      let figure = {
        filename: annotation.filename,
        vertices: [],
        konvaPoints: [],
        annotationPoints: figurePoints,
      };
      figures.push(figure);
    }
  }
  return figures;
}

function destroyAnnotationShapes(app) {
  const layerGroup = app.getActiveLayerGroup();
  const drawLayer = layerGroup.getActiveDrawLayer();
  const konvaLayer = drawLayer.getKonvaLayer();
  let annotationShapes = konvaLayer
    .find("." + anchorShapeName)
    .concat(konvaLayer.find("." + annotationShapeName));
  annotationShapes.forEach((shape) => {
    shape.destroy();
  });
}

function getPlaneConstant(app, sliceIndex) {
  let constant = 0;
  let dicomBox = app.dicomBox;
  let offset = (sliceIndex - 1) / app.max; //(app.max - 1);
  if (app.index == 0) {
    constant = dicomBox.min.y + dicomBox.size.y * offset;
  }
  if (app.index == 1) {
    constant = dicomBox.min.x + dicomBox.size.x * offset;
  }
  if (app.index == 2) {
    offset = (app.max - sliceIndex + 1) / app.max;
    constant = dicomBox.min.z + dicomBox.size.z * offset;
  }
  return constant;
}

function convertToBezierPoint(p, coefX, coefY, drawBox, appIndex, normal) {
  if (drawBox) {
    let newX = p.x / coefX;
    let newY = p.y / coefY;

    newX = appIndex == 1 ? drawBox.max.x - newX : drawBox.min.x + newX;
    newY = appIndex == 0 ? drawBox.max.y - newY : drawBox.min.y + newY;

    let point = {
      x: newX,
      y: newY,
    };
    // let vector = null;
    return point;
  }
}

function convertToKonvaPoint(p, coefX, coefY, drawBox, appIndex) {
  if (drawBox) {
    let newX = appIndex == 1 ? drawBox.max.x - p.x : p.x - drawBox.min.x;
    let newY = appIndex == 0 ? drawBox.max.y - p.y : p.y - drawBox.min.y;
    //-p.y - drawBox.min.y;

    let point = {
      x: newX * coefX,
      y: newY * coefY,
    };
    return point;
  }
}

function getControlPoints(x0, y0, x1, y1, x2, y2, t) {
  //  x0,y0,x1,y1 are the coordinates of the end (knot) pts of this segment
  //  x2,y2 is the next knot -- not connected here but needed to calculate p2
  //  p1 is the control point calculated here, from x1 back toward x0.
  //  p2 is the next control point, calculated here and returned to become the
  //  next segment's p1.
  //  t is the 'tension' which controls how far the control points spread.

  //  Scaling factors: distances from this knot to the previous and following knots.
  var d01 = Math.sqrt(Math.pow(x1 - x0, 2) + Math.pow(y1 - y0, 2));
  var d12 = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

  var fa = (t * d01) / (d01 + d12);
  var fb = t - fa;

  var p1x = x1 + fa * (x0 - x2);
  var p1y = y1 + fa * (y0 - y2);

  var p2x = x1 - fb * (x0 - x2);
  var p2y = y1 - fb * (y0 - y2);

  return [p1x, p1y, p2x, p2y];
}

function hexToCanvasColor(hexColor, opacity) {
  opacity = opacity || "1.0";
  hexColor = hexColor.replace("#", "");
  var r = parseInt(hexColor.substring(0, 2), 16);
  var g = parseInt(hexColor.substring(2, 4), 16);
  var b = parseInt(hexColor.substring(4, 6), 16);
  return "rgba(" + r + "," + g + "," + b + "," + opacity + ")";
}

function fillKonvaPoints(figure, app, drawBox, coefX, coefY) {
  if (figure && !figure.konvaPoints) {
    figure.konvaPoints = [];
    if (!app.parentApp && figure.vertices) {
      let verticePoints = [];
      if (!coefX) coefX = app.baseSize.x / drawBox.size.x;
      if (!coefY) coefY = app.baseSize.x / drawBox.size.x;
      // let spacingY = app.spacing[1];

      // if (app.index == 0) {
      if (!figure.perimeter) {
        figure.perimeter = getFigurePerimeter(figure);
      }
      const requiredDistance = Math.min(figure.perimeter / 4, anchorPointsMaxD);
      let distance = 0;
      figure.vertices.forEach((p, index) => {
        if (distance >= requiredDistance) {
          verticePoints.push(p);
          distance = 0;
        }
        distance += p.distance;
      });
      if (distance >= requiredDistance / 2) {
        verticePoints.push(figure.vertices[0]);
      }

      verticePoints.forEach((p) =>
        figure.konvaPoints.push(
          convertToKonvaPoint(p, coefX, coefY, drawBox, app.index),
        ),
      );
      // } else {
      //   figure.isStraight = true;
      //   let prevY = Infinity;
      //   figure.vertices.forEach((p, index) => {
      //     let kp = convertToKonvaPoint(p, coefX, coefY, drawBox, app.index);
      //     // let rem = kp.y % spacingY;
      //     kp.y = Math.floor(kp.y);
      //     if (kp.y !== prevY) {
      //       if (prevY != Infinity) {
      //         let mp = kp;
      //         let diff = Math.abs(prevY - mp.y);
      //         while (diff > 1) {
      //           let midY = prevY < mp.y ? mp.y - 1 : mp.y + 1;
      //           mp = { x: mp.x, y: midY };
      //           figure.konvaPoints.push(mp);
      //           diff = Math.abs(prevY - mp.y);
      //         }
      //       }
      //       figure.konvaPoints.push(kp);
      //       prevY = kp.y;
      //     }
      //   });
      // }
    }
  }
}

function getAnnotationData(annotation, app) {
  let modelGeometry = getAnnotationGeometry(annotation, app);
  if (!modelGeometry) return null;

  let vmesh = new THREE.Mesh(modelGeometry);
  const stlExporter = new STLExporter();
  // const data = stlExporter.parse(mesh);
  // const blob = new Blob([data], { type: 'text/plain' });
  const data = stlExporter.parse(vmesh, { binary: true });
  const blob = new Blob([data], { type: "application/octet-stream" });
  return blob;
}

function getAnnotationGeometry(annotation, app) {
  if (annotation.isNerve) {
    let mesh = viewer.get_model_mesh(annotation.model.id);
    if (mesh && mesh.geometry) {
      mesh.updateMatrixWorld();
      let geometry = mesh.geometry.clone();
      geometry.applyMatrix4(mesh.matrixWorld);
      return geometry;
    }
  }

  let modelGeometry = null;
  try {
    let app = Apps[annotation.appIndex];
    let meshes = getAnnotationMeshes(annotation, app);
    if (meshes && meshes.length) {
      let geometries = [];
      meshes.forEach((b) => {
        b.updateMatrixWorld();
        let geometry = b.geometry;
        if (geometry.index) geometry = geometry.toNonIndexed();
        geometry.applyMatrix4(b.matrixWorld);
        geometry.deleteAttribute("uv");
        geometry.attributes.position.gpuType = 1015;
        geometry.attributes.normal.gpuType = 1015;
        geometries.push(geometry);
      });

      modelGeometry = BufferGeometryUtils.mergeGeometries(geometries, true);
      modelGeometry.deleteAttribute("normal");
      modelGeometry = BufferGeometryUtils.mergeVertices(modelGeometry); // , 1
      modelGeometry.computeVertexNormals();
      modelGeometry.computeBoundingBox();
    }
  } catch (error) {
    console.log("ERROR getAnnotationGeometry", error);
  }

  return modelGeometry;
}

function getAnnotationMeshes(annotation, app) {
  let noSmoothCrown = app.isNoSmoothCrown;
  console.log("getAnnotationMeshes", noSmoothCrown);
  // if (annotation.isBone) {
  //   let model = annotation.model;
  //   let mesh = viewer.get_model_mesh(model.id);
  //   return [mesh];
  // }

  let meshes = [];

  let dicomBox = app.dicomBox;
  if (
    !annotation.allFigures ||
    (!annotation.isAdded && !annotation.calculated)
  ) {
    let allFigures = getAllFiguresForModel(
      annotation.model,
      app,
      dicomBox,
      true,
    );
    if (annotation.allFigures) {
      for (var sliceIndex in annotation.allFigures) {
        allFigures[sliceIndex] = annotation.allFigures[sliceIndex];
      }
    }
    annotation.allFigures = allFigures;
    annotation.calculated = true;
  }

  const drawBox2D = getDrawBox2D(dicomBox, app);
  const coefX = app.baseSize.x / drawBox2D.size.x;
  const coefY = app.baseSize.y / drawBox2D.size.y;

  let annotationPoints = [];
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity;
  for (var sliceIndex in annotation.allFigures) {
    // console.log("!!! sliceIndex", sliceIndex);
    let sliceY = getPlaneConstant(app, +sliceIndex);
    let figures = annotation.allFigures[sliceIndex];
    if (!figures) continue;
    for (const figure of figures) {
      fillKonvaPoints(figure, app, drawBox2D, coefX, coefY);

      if (figure.konvaPoints) {
        if (!figure.annotationPoints) {
          figure.annotationPoints = getKonvaAnnotationPoints(figure);
        }

        figure.annotationPoints.forEach((point) => {
          if (point) {
            let voxelPoint = convertToBezierPoint(
              point,
              coefX,
              coefY,
              drawBox2D,
              app.index,
            );
            let x = voxelPoint.x;
            let y = sliceY;
            let z = voxelPoint.y;

            ///
            if (app.index == 2) {
              y = voxelPoint.y;
              z = sliceY;
            }

            if (isNumeric(x) && isNumeric(y) && isNumeric(z)) {
              annotationPoints.push({ x, y, z });
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (z < minZ) minZ = z;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
              if (z > maxZ) maxZ = z;
            } else {
              console.log("invalid voxel point", point, voxelPoint);
            }
          }
        });
      }
    }
  }

  if (annotationPoints.length == 0) {
    return;
  }

  let figureBB = {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ },
  };
  setBoundingBoxCenter(figureBB);
  // drawBox(figureBB);

  let effect = new MarchingCubes(
    500,
    meshBasicMaterial,
    false,
    false,
    // annotationPoints.length * 2
    Math.max(maxMarchingCubesPolyCount, annotationPoints.length * 2),
  );
  // effect.init(200);
  // effect.reset();

  // let geoms = [];
  // let simpleGeometry = new THREE.BoxGeometry(0.25, 0.25, 0.25);

  let spCoef = 1 / app.spacing[2]; //app.spacing[0];

  annotationPoints.forEach((point) => {
    let x = Math.round((point.x - minX) * spCoef) + 2;
    let y = Math.round((point.y - minY) * spCoef) + 2;
    let z = Math.round((point.z - minZ) * spCoef) + 2;
    effect.setCell(x, y, z, 10000);

    // let voxelGeometry = simpleGeometry
    //   .clone()
    //   .translate(point.x, point.y, -point.z);
    // geoms.push(voxelGeometry);
  });
  effect.update();

  // console.log("MarchingCubes", effect);
  let marchingGeometry = effect.geometry.clone();
  marchingGeometry.deleteAttribute("normal");
  marchingGeometry.deleteAttribute("uv");
  let positionData = marchingGeometry.attributes.position.array;
  let positionLength = positionData.length;
  let zeroIndex = positionData.indexOf(0);

  while (
    zeroIndex > 0 &&
    zeroIndex < positionLength - 1 &&
    positionData[zeroIndex + 1] != 0
  ) {
    zeroIndex = positionData.indexOf(0, zeroIndex + 1);
  }
  if (zeroIndex > 0) {
    positionData = positionData.subarray(0, zeroIndex);
  }

  positionData = positionData.filter(function (value) {
    return !Number.isNaN(value);
  });

  marchingGeometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positionData, 3),
  );

  marchingGeometry = BufferGeometryUtils.mergeVertices(marchingGeometry);
  marchingGeometry.computeVertexNormals();
  marchingGeometry.normalizeNormals();
  marchingGeometry.computeBoundingBox();

  let marchingBB = marchingGeometry.boundingBox.clone();
  setBoundingBoxCenter(marchingBB);
  // drawBox(marchingBB);

  let coef = figureBB.size.x / marchingBB.size.x;
  console.log("bb coef", coef);

  marchingGeometry.translate(
    -marchingBB.center.x,
    -marchingBB.center.y,
    -marchingBB.center.z,
  );
  marchingGeometry.scale(coef, coef, coef);
  marchingGeometry.translate(
    figureBB.center.x,
    figureBB.center.y,
    figureBB.center.z,
  );

  // let meshGeometry = BufferGeometryUtils.mergeGeometries(geoms);
  // const mesh = new THREE.Mesh(meshGeometry, meshBasicMaterial);
  // viewer.scene.add(mesh);

  // const vmesh = new THREE.Mesh(marchingGeometry, meshBasicMaterial);
  // viewer.scene.add(vmesh);
  // drawBox(marchingGeometry.boundingBox);

  let smoothedGeom = marchingGeometry;
  let factor = annotation.smoothFactor ? +annotation.smoothFactor : 1;
  // factor -= 1;
  factor += 3;
  if (factor > 0) {
    smoothedGeom = runSmoothing(marchingGeometry, factor);
  }
  const vmesh = new THREE.Mesh(smoothedGeom, meshBasicMaterial);
  meshes.push(vmesh);

  return meshes;
}

function setBoundingBoxCenter(bb) {
  bb.size = {
    x: bb.max.x - bb.min.x,
    y: bb.max.y - bb.min.y,
    z: bb.max.z - bb.min.z,
  };

  bb.center = {
    x: bb.min.x + bb.size.x / 2,
    y: bb.min.y + bb.size.y / 2,
    z: bb.min.z + bb.size.z / 2,
  };
}

//#region Figure Help Methods

function getAnnotationPoints(figure, app, drawBox, coefX, coefY) {
  let annotationPoints = new Map();
  if (figure.vertices) {
    figure.vertices.forEach((p) => {
      let kp = convertToKonvaPoint(p, coefX, coefY, drawBox, app.index);
      let x = Math.round(kp.x);
      let y = Math.round(kp.y);
      const str = `${x},${y}`;
      annotationPoints.set(str, { x, y });
    });
    fillAnnotationPoints(annotationPoints);
    fillAnnotationPoints(annotationPoints);
  }
  return annotationPoints;
}

function getAnnotationPointsNew(figure, app, drawBox, coefX, coefY) {
  let prevX = Infinity;
  let prevY = Infinity;
  let annotationPoints = new Map();
  if (figure.vertices) {
    figure.vertices.forEach((p) => {
      let kp = convertToKonvaPoint(p, coefX, coefY, drawBox, app.index);
      let x = Math.floor(kp.x); // floor round
      let y = Math.floor(kp.y);
      const str = `${x},${y}`;

      if (
        (x !== prevX && prevX != Infinity) ||
        (y !== prevY && prevY != Infinity)
      ) {
        let diffX = Math.abs(prevX - x);
        let diffY = Math.abs(prevY - y);

        if (diffX > 30 || diffY > 30) {
          console.log(
            `figure: ${figure.filename}, diffX - ${diffX}, diffY - ${diffY}`,
          );

          // ctx.fillStyle = "green";
          // ctx.fillRect(prevX, prevY, 1, 1);
          // ctx.fillStyle = "red";
          // ctx.fillRect(x, y, 1, 1);
        }

        let px = prevX;
        let py = prevY;

        // while (diffY > 1) {
        while (diffX > 1 || diffY > 1) {
          if (diffX > 1) px = px > x ? px - 1 : px + 1;
          if (diffY > 1) py = py > y ? py - 1 : py + 1;

          const str = `${px},${py}`;
          annotationPoints.set(str, { x: px, y: py });

          diffX = Math.abs(px - x);
          diffY = Math.abs(py - y);
        }
      }

      let ap = { x, y };
      annotationPoints.set(str, ap);

      prevX = x;
      prevY = y;
    });
    fillAnnotationPoints(annotationPoints);
  }
  return annotationPoints;
}

function getKonvaAnnotationPoints(figure) {
  let pts = [];
  figure.konvaPoints.forEach((konvaPoint) => {
    pts.push(konvaPoint.x, konvaPoint.y);
  });

  let cp = [];
  let length = pts.length;
  pts.push(pts[0], pts[1], pts[2], pts[3]);
  pts.unshift(pts[length - 1]);
  pts.unshift(pts[length - 1]);
  for (var i = 0; i < length; i += 2) {
    cp = cp.concat(
      getControlPoints(
        pts[i],
        pts[i + 1],
        pts[i + 2],
        pts[i + 3],
        pts[i + 4],
        pts[i + 5],
        0.5,
      ),
    );
  }
  cp = cp.concat(cp[0], cp[1]);
  let annotationPoints = new Map();
  for (let i = 2, j = 0; i < length + 2; i += 2, j++) {
    let kp = figure.konvaPoints[j];
    kp.p0 = { x: pts[i], y: pts[i + 1] };
    kp.p1 = { x: cp[2 * i - 2], y: cp[2 * i - 1] };
    kp.p2 = { x: cp[2 * i], y: cp[2 * i + 1] };
    kp.p3 = { x: pts[i + 2], y: pts[i + 3] };

    let kpNext = figure.konvaPoints[j + 1];
    if (!kpNext) {
      kpNext = figure.konvaPoints[0];
    }

    let width = Math.abs(kp.x - kpNext.x);
    let height = Math.abs(kp.y - kpNext.y);
    let max = Math.max(width, height);
    let splitPoints = getSplitPoints(kp, max * 2);
    splitPoints.forEach((p) => {
      let x = Math.floor(p.x);
      let y = Math.floor(p.y);
      const str = `${x},${y}`;
      annotationPoints.set(str, { x, y });
    });
  }
  fillAnnotationPoints(annotationPoints);
  return annotationPoints;
}

function fillAnnotationPoints(annotationPoints) {
  if (annotationPoints) {
    let columns = new Map();
    let rows = new Map();
    annotationPoints.forEach((p) => {
      if (!columns.has(p.x)) {
        columns.set(p.x, []);
      }
      columns.get(p.x).push(p.y);
      if (!rows.has(p.y)) {
        rows.set(p.y, []);
      }
      rows.get(p.y).push(p.x);
    });

    columns.forEach((yset, x) => {
      const arr = yset.sort((a, b) => a - b);
      let y0 = arr[0];
      let yl = arr[arr.length - 1];
      if (arr.length == 1) {
      }

      for (let y = y0 + 1; y < yl; y++) {
        const str = `${x},${y}`;
        annotationPoints.set(str, { x, y });
      }
    });

    rows.forEach((xset, y) => {
      const arr = xset.sort((a, b) => a - b);
      let x0 = arr[0];
      let xl = arr[arr.length - 1];
      if (arr.length == 1) {
      }

      for (let x = x0 + 1; x < xl; x++) {
        const str = `${x},${y}`;
        annotationPoints.set(str, { x, y });
      }
    });
  }
}

function fillAnnotationPointsNew(annotationPoints) {
  if (annotationPoints) {
    let rows = new Map();
    annotationPoints.forEach((p, str) => {
      if (!rows.has(p.y)) {
        rows.set(p.y, []);
      }
      let arr = rows.get(p.y);
      if (!arr.includes(p.x)) arr.push(p.x);
    });
    // let borderKeys = new Set(annotationPoints.keys());
    // console.log(borderKeys);

    rows = new Map([...rows.entries()].sort((e1, e2) => e1[0] - e2[0]));
    let keys = [...rows.keys()];
    // let minY = keys[0];
    // let maxY = keys[rows.size - 1];
    // console.log(`rows size: ${rows.size}, minY: ${minY}, maxY: ${maxY}`);

    let startPoint = null;
    rows.forEach((xset, y) => {
      const sorted = xset.sort((a, b) => a - b);
      rows.set(y, sorted);

      if (!startPoint && sorted.length > 1 && sorted[1] - sorted[0] > 1) {
        startPoint = { x: sorted[0] + 1, y: y };
      }
    });

    while (startPoint) {
      const pixelsToCheck = [startPoint.x, startPoint.y];
      while (pixelsToCheck.length > 0) {
        const y = pixelsToCheck.pop();
        const x = pixelsToCheck.pop();
        const str = `${x},${y}`;

        if (!annotationPoints.has(str) && annotationPoints.size < 100000) {
          annotationPoints.set(str, { x, y });
          pixelsToCheck.push(x + 1, y);
          pixelsToCheck.push(x - 1, y);
          pixelsToCheck.push(x, y + 1);
          pixelsToCheck.push(x, y - 1);
        }
      }

      startPoint = null;
    }
  }
}

function getSplitPoints(kp, splitCount) {
  const splitPoints = [];
  for (let i = 1; i < splitCount; i++) {
    let splitPoint = splitBezierCurve(
      kp.p0,
      kp.p1,
      kp.p2,
      kp.p3,
      i / splitCount,
    );
    splitPoints.push(splitPoint);
  }
  return splitPoints;
}

function splitBezierCurve(p0, p1, p2, p3, proportion) {
  function lerp(a, b, t) {
    var s = 1 - t;
    return { x: a.x * s + b.x * t, y: a.y * s + b.y * t };
  }

  var p4 = lerp(p0, p1, proportion);
  var p5 = lerp(p1, p2, proportion);
  var p6 = lerp(p2, p3, proportion);
  var p7 = lerp(p4, p5, proportion);
  var p8 = lerp(p5, p6, proportion);
  var p9 = lerp(p7, p8, proportion);

  return p9;
}

function updateFigureMinMax(figure, baseOnVertices) {
  figure.min = { x: Infinity, y: Infinity };
  figure.max = { x: -Infinity, y: -Infinity };

  if (baseOnVertices && figure.vertices) {
    figure.vertices.forEach((point) => {
      if (figure.min.x > point.x) figure.min.x = point.x;
      if (figure.min.y > point.y) figure.min.y = point.y;
      if (figure.max.x < point.x) figure.max.x = point.x;
      if (figure.max.y < point.y) figure.max.y = point.y;
    });
  } else {
    if (figure.konvaPoints && figure.konvaPoints.length) {
      figure.konvaPoints.forEach((point) => {
        if (figure.min.x > point.x) figure.min.x = point.x;
        if (figure.min.y > point.y) figure.min.y = point.y;
        if (figure.max.x < point.x) figure.max.x = point.x;
        if (figure.max.y < point.y) figure.max.y = point.y;
      });
    } else if (figure.annotationPoints) {
      for (const apoint of figure.annotationPoints) {
        if (figure.min.x > apoint.x) {
          figure.min.x = apoint.x;
        }
        if (figure.min.y > apoint.y) {
          figure.min.y = apoint.y;
        }
        if (figure.max.x < apoint.x) {
          figure.max.x = apoint.x;
        }
        if (figure.max.y < apoint.y) {
          figure.max.y = apoint.y;
        }
      }
    }
  }

  figure.size = {
    x: figure.max.x - figure.min.x,
    y: figure.max.y - figure.min.y,
  };
  figure.center = {
    x: figure.min.x + figure.size.x / 2,
    y: figure.min.y + figure.size.y / 2,
  };
  figure.verticesLength = figure.vertices ? figure.vertices.length : 0;
  figure.sizeSquare = figure.size.x * figure.size.y;
  // console.log(
  //   `(${figure.min.x}, ${figure.min.y}), (${figure.max.x}, ${figure.max.y})`
  // );
}

//#endregion

function getExtrudeMesh(figure, y) {
  const extrudeSettings = {
    // curveSegments: figure.perimeter,
    // steps: 2,
    //   bevelEnabled: false,
    //   bevelThickness: 1,
    //   bevelSize: 1,
    //   bevelOffset: 0,
    //   bevelSegments: 1,
    bevelEnabled: false,
    depth: 0.25,
  };

  let pts = [];
  figure.bezierPoints.forEach((point) => {
    pts.push(point.x, point.y);
  });
  let cp = []; // array of control points, as x0,y0,x1,y1,...
  let n = pts.length;
  pts.push(pts[0], pts[1], pts[2], pts[3]);
  pts.unshift(pts[n - 1]);
  pts.unshift(pts[n - 1]);
  for (var i = 0; i < n; i += 2) {
    cp = cp.concat(
      getControlPoints(
        pts[i],
        pts[i + 1],
        pts[i + 2],
        pts[i + 3],
        pts[i + 4],
        pts[i + 5],
        0.5,
      ),
    );
  }
  cp = cp.concat(cp[0], cp[1]);

  const shape = new THREE.Shape();
  for (let i = 2; i < n + 2; i += 2) {
    if (i == 2) {
      shape.moveTo(pts[i], pts[i + 1]);
    }
    shape.bezierCurveTo(
      cp[2 * i - 2],
      cp[2 * i - 1],
      cp[2 * i],
      cp[2 * i + 1],
      pts[i + 2],
      pts[i + 3],
    );
  }
  const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  geometry.rotateX(THREE.MathUtils.degToRad(-90));
  // geometry.applyMatrix4(new THREE.Matrix4().makeScale(1, -1, 1));
  // geometry.scale(1, -1, 1);

  // const material = new THREE.MeshStandardMaterial({
  //   color: 0x00ff00,
  //   wireframe: true,
  // });
  const mesh = new THREE.Mesh(geometry);
  mesh.position.set(0, y - extrudeSettings.depth, 0);
  return mesh;
}

async function saveMeshFromAnnotation(annotation, app, y, extrudeSettings) {
  if (annotation) {
    const blob = getAnnotationData(annotation, app);
    if (!blob) {
      console.log("saveMeshFromAnnotation empty annotation", annotation.name);
      return;
    }
    saveBlob(blob, annotation.filename);

    let file1 = new File([blob], annotation.filename);
    const response = await sendSmoothStlRequest(file1, 6, true);
    if (response.status == 200) {
      saveBlob(response.data, `smoothed-${annotation.filename}`);
    }
  }
}

function saveAnnotationZip(app) {
  const zip = new JSZip();
  app.annotations.forEach((annotation) => {
    const ablob = getAnnotationData(annotation, app);
    if (!ablob) {
      console.log("saveAnnotationZip empty annotation", annotation.name);
    } else {
      zip.file(annotation.filename, ablob);
    }
  });
  console.log("saveAnnotationZip", zip);
  zip
    .generateAsync({
      type: "blob",
    })
    .then(function (content) {
      saveBlob(content, `AnnotationSTL_${vueApp.patientId}.zip`);
    });
}

function saveTestCurveMesh() {
  const curve = new THREE.CubicBezierCurve3(
    new THREE.Vector3(-10, 0, 0),
    new THREE.Vector3(-5, 15, 0),
    new THREE.Vector3(20, 15, 0),
    new THREE.Vector3(10, 0, 0),
  );
  const points = curve.getPoints(50);
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
  // const curveObject = new THREE.Mesh(geometry, material);
  // viewer.scene.add(curveObject);

  const pointsMaterial = new THREE.PointsMaterial({
    color: 0x0080ff,
    size: 1,
    alphaTest: 0.5,
  });
  let tpoints = new THREE.Points(geometry, pointsMaterial);
  viewer.scene.add(tpoints);

  const curve2 = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(-5, 0, 5),
    new THREE.Vector3(0, 9, 5),
    new THREE.Vector3(4, 0, 5),
  );
  const points2 = curve2.getPoints(20);
  const geometry2 = new THREE.BufferGeometry().setFromPoints(points2);
  const material2 = new THREE.MeshBasicMaterial({ color: 0x00ffff });
  // const curveObject2 = new THREE.Mesh(geometry2, material2);
  // viewer.scene.add(curveObject2);

  tpoints = new THREE.Points(geometry2, pointsMaterial);
  viewer.scene.add(tpoints);

  const allPoints = points.concat(points2);
  const cgeometry = new ConvexGeometry(allPoints);
  const cmaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
  const cmesh = new THREE.Mesh(cgeometry, cmaterial);
  // viewer.scene.add(cmesh);

  const mesh = cmesh;
  //viewer.get_model_mesh(vueApp.allTeeth[0].id);
  const stlExporter = new STLExporter();
  const data = stlExporter.parse(mesh, { binary: true });
  const blob = new Blob([data], { type: "application/octet-stream" });
  // const data = stlExporter.parse(mesh);
  // const blob = new Blob([data], { type: 'text/plain' });
  const strDataUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  document.body.appendChild(link);
  link.download = "tooth0.stl";
  link.href = strDataUrl;
  link.click();
  document.body.removeChild(link);
}

// !!!
async function updateTeethOnServer(app, smoothTeeth, saveNrrd) {
  updateSavingStatus(app, true);
  let changedAnnotations = [];
  let deletedAnnotations = [];
  for (const xapp of Apps) {
    xapp.isSaving = true;
    if (xapp.allAnnotations) {
      changedAnnotations = changedAnnotations.concat(
        xapp.allAnnotations.filter((a) => a.wasChanged),
      );
    }

    if (xapp.deletedAnnotations) {
      deletedAnnotations = deletedAnnotations.concat(
        xapp.deletedAnnotations.filter((a) => !a.isAdded),
      );
    }
    xapp.deletedAnnotations = [];
  }

  let nerveChanged = changedAnnotations.find((x) => x.isNerve);
  if (nerveChanged) {
    changedAnnotations.push({
      isNerve: true,
      filename: "merged_canals.stl",
      model: vueApp.nerves.find((n) => !n.isSingle),
    });
  }

  let numberChangesCount = getNumberChangesCount();
  const teethData = {
    deletedTeeth: [],
    changedTeeth: [],
    addedTeeth: [],
  };
  teethData.smoothTeeth = !!smoothTeeth;
  teethData.saveNrrd = !!saveNrrd;
  teethData.isStitched = !!vueApp.isStitched;
  let changesExist =
    changedAnnotations.length > 0 ||
    deletedAnnotations.length > 0 ||
    numberChangesCount > 0;

  if (!changesExist && teethData.saveNrrd) {
    changesExist = await swal({
      text: "No changes detected. Do you want to update only nrrd files?",
      buttons: {
        yes: { text: "Yes", value: true },
        no: { text: "No", value: false },
      },
    });
  }
  if (changesExist) {
    const zip = new JSZip();
    changedAnnotations.forEach(async (annotation) => {
      let filename = annotation.filename;
      if (annotation.isAdded) {
        teethData.changedTeeth.push(filename);
        let addedTooth = {
          number: +annotation.number,
          numberType: annotation.numberType,
          remarks: annotation.remarks,
          filename: filename,
        };
        teethData.addedTeeth.push(addedTooth);
      } else {
        filename = getToothFileName(annotation.model);
        teethData.changedTeeth.push(filename);
      }
      annotation.wasChanged = false;
      annotation.wasSaved = true;

      if (
        annotation.isNerve &&
        annotation.sortedPoints &&
        annotation.sortedPoints.length > 0
      ) {
        let fileName = annotation.model.isLeft
          ? `left_segment_points.txt`
          : `right_segment_points.txt`;
        const string_arr = [];
        for (const point of annotation.sortedPoints) {
          let pos = point.dicomPos;
          const line = `${-pos.x}, ${-pos.y}, ${pos.z}`;
          string_arr.push(line);
        }

        const text = string_arr.join("\n");
        const textBlob = new Blob([text], { type: "text/plain;charset=utf-8" });
        zip.file(fileName, textBlob);
      }

      // const ablob = getAnnotationData(annotation, app);
      let linkedAnnotation = annotation;
      if (annotation.linkedAnnotation) {
        linkedAnnotation = annotation.linkedAnnotation;
      }
      let linkedApp = Apps[linkedAnnotation.appIndex];
      let modelGeometry = getAnnotationGeometry(linkedAnnotation, linkedApp);
      if (modelGeometry) {
        let vmesh = new THREE.Mesh(modelGeometry);
        vmesh.geometry.translate(
          defaultXOffset,
          defaultYOffset,
          defaultZOffset,
        );
        let matrix1 = new THREE.Matrix4();
        matrix1.makeRotationAxis(new THREE.Vector3(1, 0, 0), -rotateX);
        vmesh.geometry.applyMatrix4(matrix1);
        let matrix2 = new THREE.Matrix4();
        matrix2.makeRotationAxis(new THREE.Vector3(0, 1, 0), -rotateY);
        vmesh.geometry.applyMatrix4(matrix2);
        vmesh.geometry.computeBoundingBox();

        const stlExporter = new STLExporter();
        const data = stlExporter.parse(vmesh, { binary: true });
        const blob = new Blob([data], { type: "application/octet-stream" });
        zip.file(filename, blob);

        let zipFile = zip.file(filename);
        let buffer = await zipFile.async("arraybuffer");
        let isValid = validateStlArrayBuffer(buffer, vmesh.geometry);
        if (!isValid) {
          swal({
            text: `Error while saving ${annotation.name}. Please contact support@cephx.com`,
          });
          updateSavingStatus(app, false);
          return;
        }
        // let promise = zipFile.async("arraybuffer");
        // promise.then((buffer) => {
        //   validateStlArrayBuffer(buffer, vmesh)
        // });

        // annotation.isAdded = false;
      }
    });
    deletedAnnotations.forEach((a) => {
      let filename = getToothFileName(a.model);
      teethData.deletedTeeth.push(filename);
    });
    // teethData.deletedTeeth.push("tooth_12-added.stl");

    if (teethData.saveNrrd) {
      app.allAnnotations.forEach((annotation) => {
        let linkedAnnotation = annotation;
        if (annotation.linkedAnnotation) {
          linkedAnnotation = annotation.linkedAnnotation;
        }
        let linkedApp = Apps[linkedAnnotation.appIndex];
        if (
          linkedAnnotation.allAnnotationPoints &&
          linkedAnnotation.allAnnotationPoints.length
        ) {
          const ablob = getAnnotationNrrdBlob(linkedApp, linkedAnnotation);
          if (ablob) {
            zip.file(annotation.filename.replace(".stl", ".nrrd"), ablob);
          } else {
            console.error("updateTeethOnServer empty nrrd", annotation.name);
          }
        }
      });
    }

    zip.file("teethData.json", JSON.stringify(teethData));

    let numberChanges = getNumberChanges(true);
    zip.file("numberChanges.json", JSON.stringify(numberChanges));

    vueApp.isStitching = vueApp.isStitched && teethData.changedTeeth.length > 0;
    console.log("updateTeethOnServer", vueApp.isStitching, zip);
    zip
      .generateAsync({
        type: "blob",
      })
      .then(async (content) => {
        try {
          await sendUpdateTeethRequest(content);
        } catch (e) {
          console.error("!!! ERROR SAVING CHANGES", e);
          changedAnnotations.forEach((annotation) => {
            annotation.wasChanged = true;
          });
          swal({
            text: `Error while saving changes. Please contact support@cephx.com`,
          });
        }
        updateSavingStatus(app, false);
      });
  } else {
    console.log("No dicom changes detected");
    updateSavingStatus(app, false);
    if (!teethData.saveNrrd)
      swal({
        text: `No changes detected`,
      });
  }
}

function updateSavingStatus(app, value) {
  if (app) app.isSaving = value;
  vueApp.isAnnotationSaving = value;
  for (const xapp of Apps) {
    xapp.isSaving = value;
  }
}

function validateStlArrayBuffer(arrayBuffer, originalGeometry) {
  try {
    let loader = new STLLoader();
    const geometry = loader.parse(arrayBuffer);
    geometry.computeBoundingBox();
    console.log("validateStlArrayBuffer", geometry, originalGeometry);
    return (
      arrayBuffer.byteLength > 1000 &&
      compareBoxes(geometry.boundingBox, originalGeometry.boundingBox)
    );
  } catch (error) {
    console.log(error);
    return false;
  }
}

function compareBoxes(box1, box2) {
  let dif = 0.01;
  return (
    box1 &&
    box2 &&
    box1.min &&
    box2.min &&
    box1.max &&
    box2.max &&
    Math.abs(box1.min.x - box2.min.x) < dif &&
    Math.abs(box1.min.y - box2.min.y) < dif &&
    Math.abs(box1.min.z - box2.min.z) < dif &&
    Math.abs(box1.max.x - box2.max.x) < dif &&
    Math.abs(box1.max.y - box2.max.y) < dif &&
    Math.abs(box1.max.z - box2.max.z) < dif
  );
}

function getSliceIndex(app) {
  const layerGroup = app.getActiveLayerGroup();
  const activeLayer = layerGroup.getActiveViewLayer();
  const viewController = activeLayer.getViewController();
  const currentIndex = viewController.getCurrentIndex();
  return currentIndex.get(2);
}

//#region NRRD

function mergeTypedArrays(a, b, withLF) {
  if (!a && !b) throw "Please specify valid arguments for parameters a and b.";

  if (!b || b.length === 0) return a;
  if (!a || a.length === 0) return b;

  if (Object.prototype.toString.call(a) !== Object.prototype.toString.call(b))
    throw "The types of the two arguments passed for parameters a and b do not match.";

  let length = a.length + b.length;
  if (withLF) length++;
  let c = new a.constructor(length);
  c.set(a);
  if (withLF) c[a.length] = 0x0a;
  c.set(b, withLF ? a.length + 1 : a.length);

  return c;
}

function initAppNrrdMeta(app) {
  if (app && !app.nrrdMeta) {
    // https://ivmartel.github.io/dwv/doc/stable/Geometry.html

    if (!app.geometry) {
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const activeLayer = layerGroup.getActiveViewLayer();
      const viewController = activeLayer.getViewController();
      app.geometry = viewController.getViewImage().getGeometry();
    }
    const geometry = app.geometry;
    //app.getImage(0).getGeometry(); // getLastImage getImage(0)
    app.nrrdMeta = {};

    const size = geometry.getSize().getValues();
    let sizeX = (app.nrrdMeta.sizeX = +size[0].toFixed(3));
    let sizeY = (app.nrrdMeta.sizeY = +size[1].toFixed(3));
    let sizeZ = (app.nrrdMeta.sizeZ = +size[2].toFixed(3));

    const spacing = geometry.getSpacing().getValues(); // getRealSpacing getSpacing
    let spX = (app.nrrdMeta.spX = +spacing[0].toFixed(3));
    let spY = (app.nrrdMeta.spY = +spacing[1].toFixed(3));
    let spZ = (app.nrrdMeta.spZ = +spacing[2].toFixed(3));

    const origin = geometry.getOrigin();
    let originX = (app.nrrdMeta.originX = +origin.getX().toFixed(3));
    let originY = (app.nrrdMeta.originY = +origin.getY().toFixed(3));
    let originZ = (app.nrrdMeta.originZ = +origin.getZ().toFixed(3));

    let header = {};
    header["type"] = "unsigned char";
    header["dimension"] = "3";
    header["space"] = "3D-right-handed"; // ?
    header["sizes"] = `${sizeX} ${sizeY} ${sizeZ}`;
    header["space directions"] = `(${spX},0,0) (0,${spY},0) (0,0,${spZ})`;

    header["centerings"] = "cell cell cell";
    header["kinds"] = "space space space";
    header["encoding"] = "gzip"; //"raw"; "gzip";
    header["space origin"] = `(${originX},${originY},${originZ})`;
    header["measurement frame"] = "(1,0,0) (0,1,0) (0,0,1)"; // ?

    app.nrrdMeta.header = header;
    app.nrrdMeta.headerArray = convertNrrdHeaderToArray(header);

    app.nrrdMeta.width = sizeX;
    app.nrrdMeta.height = sizeY;
    app.nrrdMeta.depth = sizeZ;
    app.nrrdMeta.sliceSize = sizeX * sizeY;
    console.log("initAppNrrdMeta", app.nrrdMeta);
  }
}

function convertNrrdHeaderToArray(header) {
  let headerText = "NRRD0005\n# Generated at ORCA Dental AI Viewer\n";
  for (const key in header) {
    if (Object.hasOwnProperty.call(header, key)) {
      const value = header[key];
      headerText += `${key}: ${value}\n`;
    }
  }
  const encoder = new TextEncoder();
  const headerArray = encoder.encode(headerText);

  return headerArray;
}

function getAnnotationNrrdBlob(app, annotation) {
  let blob = null;
  if (app && annotation) {
    initAppNrrdMeta(app);

    let spX = app.nrrdMeta.spX;
    let spY = app.nrrdMeta.spY;
    let spZ = app.nrrdMeta.spZ;
    let originX = app.nrrdMeta.originX;
    let originY = app.nrrdMeta.originY;
    let originZ = app.nrrdMeta.originZ;

    let width = app.nrrdMeta.width;
    let height = app.nrrdMeta.height;
    let depth = app.nrrdMeta.depth;
    let sliceSize = app.nrrdMeta.sliceSize;
    let headerArray = app.nrrdMeta.headerArray;
    let header = app.nrrdMeta.header;

    let allAnnotationPoints = annotation.allAnnotationPoints;
    if (app.index == 2) {
      // console.log("getAnnotationNrrdBlob", app.index);
      allAnnotationPoints = [];
      for (const point of annotation.allAnnotationPoints) {
        allAnnotationPoints.push({ x: point.x, y: point.z, z: point.y });
      }
    }

    let all_x = allAnnotationPoints.map((p) => p.x);
    let all_y = allAnnotationPoints.map((p) => p.y);
    let all_z = allAnnotationPoints.map((p) => p.z);

    let res_x = minMaxFromArray(all_x);
    let min_x = res_x.min;
    let max_x = res_x.max;
    let size_x = max_x - min_x + 1;

    let res_y = minMaxFromArray(all_y);
    let min_y = res_y.min;
    let max_y = res_y.max;
    let size_y = max_y - min_y + 1;

    let res_z = minMaxFromArray(all_z);
    let min_z = res_z.min;
    let max_z = res_z.max;
    let size_z = max_z - min_z + 1;

    header["space origin"] = `(${originX + min_x * spX},${
      originY + min_y * spY
    },${originZ + min_z * spZ})`;
    header["sizes"] = `${size_x} ${size_y} ${size_z}`;

    width = size_x;
    height = size_y;
    sliceSize = size_x * size_y;
    depth = size_z;

    headerArray = convertNrrdHeaderToArray(header);
    let pointOffset = 0;
    let newData = new Uint8Array(sliceSize * depth);
    for (const apoint of allAnnotationPoints) {
      let sliceOffset = (apoint.z - min_z) * sliceSize;
      let row = apoint.y - min_y;
      let column = apoint.x - min_x;
      let rowOffset = sliceOffset + row * width;
      pointOffset = rowOffset + column;
      newData[pointOffset] = 1;
      if (pointOffset == 51) {
        console.log("");
      }
    }

    let newGzipData = pako.gzip(newData);
    let newBuffer = mergeTypedArrays(headerArray, newGzipData, true);
    blob = new Blob([newBuffer]);
  }
  return blob;
}

function minMaxFromArray(arr) {
  let len = arr.length;
  let max = -Infinity;
  let min = Infinity;

  while (len--) {
    let current = arr[len];
    if (current > max) max = current;
    if (current < min) min = current;
  }
  return { max: max, min: min };
}

function saveAnnotationNrrd(app, annotation) {
  console.log("saveAnnotationNrrd", annotation);
  if (app && annotation) {
    // let figures = [];
    // for (const figure of annotation.figures) {
    //   figures.push({
    //     annotationPoints: Object.fromEntries(figure.annotationPoints),
    //   });
    // }
    // saveLocalJson(figures, `${annotation.name}_figures.json`);
    const blob = getAnnotationNrrdBlob(app, annotation);
    if (blob) saveBlob(blob, `${annotation.name}.nrrd`);
  }
}

function saveAllAnnotationNrrds(app) {
  if (app) {
    console.log("saveAllAnnotationNrrds", app);
    initAppNrrdMeta(app);

    const zip = new JSZip();
    app.annotations.forEach((annotation) => {
      // if (annotation.isTooth) {
      const ablob = getAnnotationNrrdBlob(app, annotation);
      if (ablob) {
        zip.file(
          `${
            annotation.isTooth
              ? annotation.name
              : annotation.model.name.toLowerCase()
          }.nrrd`,
          ablob,
        );
      } else {
        console.error(
          "saveAllAnnotationNrrds empty blob annotation",
          annotation.name,
        );
      }
      // }
    });
    zip
      .generateAsync({
        type: "blob",
      })
      .then(function (content) {
        saveBlob(content, `pat_${vueApp.patientId}_nrrd.zip`);
      });
  }
}

//#endregion

export {
  saveTestCurveMesh,
  drawAnnotations,
  hexToCanvasColor,
  saveMeshFromAnnotation,
  saveAnnotationZip,
  updateTeethOnServer,
  getAnnotationData,
  getAnnotationGeometry,
  saveAnnotationNrrd,
  saveAllAnnotationNrrds,
  calculateAllFigures,
  destroyAnnotationShapes,
  updateFigureMinMax,
};
